"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useMotion } from "@/contexts/MotionContext";

interface ServicesDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  /** From `SITE_SURFACES` via `resolveTopNavItems` (e.g. “Our Services”). */
  label: string;
  overviewHref: string;
  /** Service pillar links; paths/labels from `SITE_SURFACES`. */
  serviceLinks: ReadonlyArray<{ href: string; label: string }>;
}

export default function ServicesDropdown({
  isOpen,
  onToggle,
  label,
  overviewHref,
  serviceLinks,
}: ServicesDropdownProps) {
  const { isMotionEnabled } = useMotion();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          onToggle();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      <button
        type="button"
        onClick={onToggle}
        className="group relative flex items-center gap-1 px-1 py-2 transition-all duration-300 hover:text-gray-light dark:hover:text-gray-dark"
      >
        <span>{label}</span>
        <motion.svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className="ml-1"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.svg>
        <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-current opacity-0 transition-all duration-300 group-hover:w-full group-hover:opacity-100" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute left-0 top-full z-[110] mt-2 min-w-[200px] rounded border border-gray-200 bg-background-light py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
            initial={
              isMotionEnabled ? { opacity: 0, y: -10, scale: 0.95 } : false
            }
            animate={isMotionEnabled ? { opacity: 1, y: 0, scale: 1 } : {}}
            exit={isMotionEnabled ? { opacity: 0, y: -10, scale: 0.95 } : {}}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              initial={isMotionEnabled ? { opacity: 0, x: -10 } : false}
              animate={isMotionEnabled ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0 }}
            >
              <Link
                href={overviewHref}
                className="block border-b border-gray-200 px-4 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                onClick={onToggle}
              >
                All Services
              </Link>
            </motion.div>

            {serviceLinks.map((category, index) => (
              <motion.div
                key={category.href}
                initial={isMotionEnabled ? { opacity: 0, x: -10 } : false}
                animate={isMotionEnabled ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: (index + 1) * 0.05 }}
              >
                <Link
                  href={category.href}
                  className="block px-4 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                  onClick={onToggle}
                >
                  {category.label}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
